import { useCallback, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import { NavLink, titleToUrl, useSelector } from '../../../imports/generalImport';
import MobileSectionTitle from '../mobileProductCard/MobileSectionTitle';


const MobileFeatureComponent = () => {
    const featuredData = useSelector((state) => state?.MobileHomepage?.allData?.featured);

    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop: true, align: 'center', containScroll: false },
        [Autoplay({ delay: 1500, stopOnInteraction: false })]
    );

    const tweenScale = useCallback((emblaApi, eventName) => {
        const engine = emblaApi.internalEngine();
        const scrollProgress = emblaApi.scrollProgress();
        const slidesInView = emblaApi.slidesInView();
        const isScrollEvent = eventName === 'scroll';

        emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
            let diffToTarget = scrollSnap - scrollProgress;
            const slidesInSnap = engine.slideRegistry[snapIndex];

            slidesInSnap.forEach((slideIndex) => {
                if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

                if (engine.options.loop) {
                    engine.slideLooper.loopPoints.forEach((loopItem) => {
                        const target = loopItem.target();

                        if (slideIndex === loopItem.index && target !== 0) {
                            const sign = Math.sign(target);

                            if (sign === -1) {
                                diffToTarget = scrollSnap - (1 + scrollProgress);
                            }
                            if (sign === 1) {
                                diffToTarget = scrollSnap + (1 - scrollProgress);
                            }
                        }
                    });
                }

                // Scale logic: Center (diff=0) -> 1, Sides -> smaller
                const tweenValue = 1 - Math.abs(diffToTarget * 1); // Factor controls sensitivity
                const scale = Math.max(0.8, Math.min(1, tweenValue)); // Scale between 0.8 and 1

                // Rotation logic: "translate some degree" -> rotateY
                const rotate = diffToTarget * 45; // 45 degrees rotation at 1 unit distance

                const slideNode = emblaApi.slideNodes()[slideIndex];
                const cleanSlideNode = slideNode.querySelector('.slide-inner');

                if (cleanSlideNode) {
                    cleanSlideNode.style.transform = `scale(${scale}) perspective(1000px) rotateY(${rotate}deg)`;
                    // Ensure smooth transition during autoplay but instant during scroll drag is tricky.
                    // Embla updates scroll position every frame so transition: 0s is best for sync.
                    cleanSlideNode.style.transition = 'transform 0s';
                }
            });
        });
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        tweenScale(emblaApi);
        emblaApi.on('scroll', tweenScale);
        emblaApi.on('reInit', tweenScale);

        return () => {
            emblaApi.off('scroll', tweenScale);
            emblaApi.off('reInit', tweenScale);
        };
    }, [emblaApi, tweenScale]);

    return (
        <Box
            component={"div"}
            sx={{
                my: 3,
                pt: 3.5,
                pb: 1.2,
                backgroundColor: "rgb(237 246 255)",
            }}
        >
            <Stack
                sx={{
                    pl: 1,
                }}
            >
                <MobileSectionTitle
                    sectionTitle={"Featured Gift Vouchers"}
                />
            </Stack>

            <Box sx={{ overflow: 'hidden', padding: '20px 0' }} ref={emblaRef}>
                <Box sx={{ display: 'flex', touchAction: 'pan-y' }}>
                    {featuredData?.map((item) => {
                        const url = `/card-details/${item?.id}/${titleToUrl(item?.title)}${item?.url_utm ? `?${item?.url_utm}` : ''}`;

                        return (
                            <Box
                                key={`featuredData-${item?.id}`}
                                sx={{
                                    flex: '0 0 auto',
                                    position: 'relative',
                                    minWidth: 0,
                                    mx: 'auto',
                                    width: {
                                        xs: '190px',
                                        sm: 310
                                    },
                                    height: {
                                        xs: '190px',
                                        sm: 190
                                    },
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    perspective: '1000px'
                                }}
                            >
                                <Box className="slide-inner" sx={{ width: '100%', height: '100%' }}>
                                    <NavLink to={url} style={{ textDecoration: 'none', display: 'block', width: '100%', height: '100%' }}>
                                        <Box
                                            component="img"
                                            src={item?.featured_img}
                                            alt={item?.title}
                                            loading="lazy"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                boxShadow: 3,
                                                backgroundColor: 'transparent',
                                                display: 'block'
                                            }}
                                        />
                                    </NavLink>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </Box>
    )
}

export default MobileFeatureComponent
